import React, { useEffect, useState } from "react";
import logo from "../assets/color logo bg remove1.svg";
import goldlogo from "../assets/gold_logo.svg";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, ShoppingBag } from "lucide-react";

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState("Home");
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [data, setData] = useState([]);
  const [cartItemCount, setCartItemCount] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://webadmin.grapits.in/api/get_extra_details",
        );
        const result = await response.json();
        if (result.status === 1) {
          setData(result.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const existingCartItems =
      JSON.parse(localStorage.getItem("cartItems")) || [];
    setCartItemCount(existingCartItems.length);
  }, []);

  const menuItems = [
    { href: "App#home", label: "Home" },
    { href: "App#services", label: "Services" },
    {
      href: "#",
      label: "Products",
      subMenu: [
        { id: 5, href: "Products", label: "Interior Wall Textures" },
        { id: 2, href: "Products", label: "Exterior Wall Textures" },
        { id: 7, href: "Products", label: "Interior & Exterior Wall Texture" },
        { id: 6, href: "Products", label: "Seamless Flooring" },
      ],
    },
    { href: "App#pricing", label: "Gallery" },
    { href: "App#about", label: "About Us" },
    { href: "contact", label: "Contact Us" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        isScrolled
          ? "bg-black bg-opacity-90 shadow-lg"
          : "bg-white backdrop-blur-sm"
      }`}
    >
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-24">
        <div className="flex justify-between items-center h-16">
          {/* Logo Section */}
          <div className="flex-shrink-0">
            <a
              href="#home"
              className="flex items-center transform transition-all duration-300 ease-in-out"
            >
              <img
                src={isScrolled ? goldlogo : logo}
                alt="Logo"
                className="h-8 w-auto object-contain sm:h-8 md:h-12 lg:h-12 xl:h-12"
              />
            </a>
          </div>
          {/* Desktop Menu */}
          <div className="hidden lg:block flex-grow max-w-3xl">
            <div className="flex items-center justify-center space-x-1 xl:space-x-4">
              {menuItems.map((item) => (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => {
                    if (hoverTimeout) clearTimeout(hoverTimeout);
                    setHoverTimeout(null);
                    setOpenSubMenu(item.label);
                  }}
                  onMouseLeave={() => {
                    const timeout = setTimeout(() => {
                      setOpenSubMenu(null);
                    }, 500);
                    setHoverTimeout(timeout);
                  }}
                >
                  <a
                    href={item.href}
                    className={`relative px-3 py-2 text-md font-medium ${
                      selectedItem === item.label
                        ? isScrolled
                          ? "gradient-gold-text"
                          : "text-[#d35400] font-bold"
                        : isScrolled
                          ? "gradient-text-white"
                          : "gradient-text"
                    } hover:text-[#d35400] transition-colors duration-300`}
                    onClick={(e) => {
                      if (!item.subMenu) {
                        setSelectedItem(item.label);
                        setOpenSubMenu(null);
                        navigate(item.href);
                      } else {
                        e.preventDefault();
                        setOpenSubMenu(item.label);
                      }
                    }}
                  >
                    {item.label}
                  </a>
                  {item.subMenu && openSubMenu === item.label && (
                    <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                      {item.subMenu.map((subItem) => (
                        <a
                          key={subItem.label}
                          href={subItem.href}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedItem(subItem.label);
                            setOpenSubMenu(null);
                            navigate(
                              `${subItem.href}/${subItem.id}/${subItem.label}`,
                            );
                          }}
                        >
                          {subItem.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="font-bold lg:hidden w-full justify-center flex items-center">
            <a
              href={`tel:${data?.phone}`}
              className={`transition-colors text-[12px] duration-300 ${
                isScrolled ? "gradient-text-white" : "gradient-text"
              }`}
            >
              {"Call us:" + data?.phone}
            </a>
          </div>
          {/* Cart Icon with Count */}
          <div className="flex items-center">
            <button className="relative p-2" onClick={() => navigate("/cart")}>
              <ShoppingBag
                className={`h-6 w-6 ${
                  isScrolled ? "text-white" : "text-black"
                }`}
              />
              {cartItemCount > 0 && (
                <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
                  {cartItemCount}
                </span>
              )}
            </button>
          </div>
          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="relative inline-flex items-center justify-center w-10 h-10 rounded-md focus:outline-none focus:ring-2 focus:ring-[#d35400] transition-colors duration-300"
              aria-expanded={isOpen}
            >
              <div className="absolute inline-flex items-center justify-center w-5 h-5">
                <span
                  className={`absolute block w-5 h-0.5 transform transition-all duration-300 ease-in-out ${
                    isOpen ? "rotate-45 translate-y-0" : "-translate-y-1.5"
                  } ${isScrolled ? "bg-white" : "bg-black"}`}
                />
                <span
                  className={`absolute block w-5 h-0.5 transform transition-all duration-300 ease-in-out ${
                    isOpen ? "opacity-0" : "opacity-100"
                  } ${isScrolled ? "bg-white" : "bg-black"}`}
                />
                <span
                  className={`absolute block w-5 h-0.5 transform transition-all duration-300 ease-in-out ${
                    isOpen ? "-rotate-45 translate-y-0" : "translate-y-1.5"
                  } ${isScrolled ? "bg-white" : "bg-black"}`}
                />
              </div>
            </button>
          </div>
          <div className="font-bold hidden sm:ml-2 lg:block flex items-center">
            <a
              href={`tel:${data?.phone}`}
              className={`transition-colors  duration-300 ${
                isScrolled ? "gradient-text-white" : "gradient-text"
              }`}
            >
              {"Call us:" + data?.phone}
            </a>
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      <div
        className={`lg:hidden text-center transition-all duration-500 ease-in-out ${
          isOpen ? "max-h-[32rem]" : "max-h-0"
        } overflow-hidden`}
      >
        <div className="px-2 py-3 space-y-1 bg-[#fff]">
          {menuItems.map((item, index) => (
            <div key={item.label}>
              <a
                href={item.href}
                className={`flex items-center justify-between block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-white hover:bg-[#d35400] transform transition-all duration-300 ease-in-out hover:translate-x-2 ${
                  selectedItem === item.label ? "bg-[#d35400] text-white" : ""
                }`}
                onClick={(e) => {
                  if (item.subMenu) {
                    e.preventDefault();
                    setOpenSubMenu(
                      openSubMenu === item.label ? null : item.label,
                    );
                  } else {
                    setOpenSubMenu(null);
                    setSelectedItem(item.label);
                    setIsOpen(false);
                    navigate(item.href);
                  }
                }}
                style={{
                  opacity: 0,
                  animation: isOpen
                    ? `slideIn 0.4s ease-out ${index * 0.1}s forwards`
                    : "none",
                }}
              >
                <span>{item.label}</span>
                {item.subMenu && (
                  <span>
                    {openSubMenu === item.label ? (
                      <ChevronUp />
                    ) : (
                      <ChevronDown />
                    )}
                  </span>
                )}
              </a>
              {item.subMenu && openSubMenu === item.label && (
                <div className="pl-4">
                  {item.subMenu.map((subItem) => (
                    <div
                      key={subItem.label}
                      href={subItem.href}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-white hover:bg-[#d35400] transform transition-all duration-300 ease-in-out hover:translate-x-2"
                      onClick={() => {
                        setSelectedItem(subItem.label);
                        setIsOpen(false);
                        setOpenSubMenu(null);
                        navigate(
                          `${subItem.href}/${subItem.id}/${subItem.label}`,
                        );
                      }}
                    >
                      {subItem.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Slide animation */}
      <style jsx>{`
        @keyframes slideIn {
          0% {
            opacity: 0;
            transform: translateX(-2rem);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </nav>
  );
};
